import { useCallback, useState } from "react";

import { Button, InfoBar, Modal } from "@shapr3d/frontend-lib";
import toast from "react-hot-toast";

import { useTracker } from "../../hooks/useTracker";
import { ModalClosedAction, ModalShownAction } from "../../lib/TrackingAdapter";

import style from "./RecoveryCodeModal.module.css";

export type RecoveryCodeModalProps = {
  open: boolean;
  recoveryCode: string;
  onClose: () => void;
};

export default function RecoveryCodeModal({ open, recoveryCode, onClose }: RecoveryCodeModalProps) {
  const tracker = useTracker();

  const [copied, setCopied] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(recoveryCode);
      setCopied(true);
      toast.success("Recovery code copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy recovery code to clipboard");
      setCopied(false);
    }
  }, [recoveryCode]);

  const handleDownload = useCallback(() => {
    const blob = new Blob([recoveryCode], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "shapr3d-recovery-code.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setDownloaded(true);

    toast.success("Recovery code downloaded as .txt!");
  }, [recoveryCode]);

  const canProceed = copied || downloaded;

  return (
    <Modal
      open={open}
      onOpen={() => tracker.trackModalShown(ModalShownAction.TWO_FACTOR_RECOVERY_CODE)}
      onClose={() => {
        tracker.trackModalClosed(ModalClosedAction.TWO_FACTOR_RECOVERY_CODE);
        onClose();
      }}
      size="medium"
      title="Save your recovery code"
      persistent
      footer={
        <div className={style.footer}>
          <Button variant="primary" onClick={onClose} disabled={!canProceed}>
            I have saved the code
          </Button>
        </div>
      }
    >
      <div className={style.recoveryCodeContainer}>
        <div className={style.recoveryCode}>{recoveryCode}</div>
        <div className={style.buttonGroup}>
          <Button variant="grayscale" onClick={handleCopy}>
            {"Copy to clipboard"}
          </Button>
          <Button variant="grayscale" onClick={handleDownload}>
            {"Download as .txt"}
          </Button>
        </div>
      </div>
      <InfoBar
        variant="critical"
        title="Important"
        text={
          <>
            <p>
              Save your recovery code in a safe place. It is used to access your account in case you
              lose access to your device or cannot access your authenticator app for some other
              reason.
            </p>
            <p>
              <b>
                For security reasons, we will not be able to help you recover your account without
                it.
              </b>
            </p>
          </>
        }
      />
    </Modal>
  );
}
